import React, { useCallback, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { xcss, Box, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	FieldWrapper,
	SideBySideField,
	FieldHeadingTitle,
	FieldHeadingSideBySide,
} from '@atlassian/jira-issue-field-heading/src/styled.tsx';
import { FieldPin } from '@atlassian/jira-issue-field-pin/src/index.tsx';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import { DEV_SUMMARY_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { NestedPanel } from '@atlassian/jira-issue-nested-panel/src/ui/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const DevelopmentFieldWrapper = ({
	children,
	afterHeadingNode,
	pinnable = false,
}: Props) => {
	const { formatMessage } = useIntl();
	const titleLabel = formatMessage(messages.label);
	return (
		<FieldWrapper>
			<DevPanelFieldHeading>
				<FieldHeadingTitle>{titleLabel}</FieldHeadingTitle>
				{afterHeadingNode}
				{pinnable && <FieldPin fieldId={DEV_SUMMARY_TYPE} label={titleLabel} />}
			</DevPanelFieldHeading>
			<SideBySideField>{children}</SideBySideField>
		</FieldWrapper>
	);
};

export const DevelopmentFieldWrapperNext = ({
	children,
	afterHeadingNode,
	pinnable = false,
}: Props) => {
	const [isNestedPanelOpen, setIsNestedPanelOpen] = useState(false);

	const onToggleNestedPanel = useCallback(() => {
		setIsNestedPanelOpen(!isNestedPanelOpen);
	}, [isNestedPanelOpen]);

	const { formatMessage } = useIntl();
	const titleLabel = formatMessage(messages.label);
	return (
		<NestedPanel
			ariaLabelExpand="Expand"
			ariaLabelCollapse="Collapse"
			contentId="dev-info-panel"
			isOpen={isNestedPanelOpen}
			label={
				<Flex xcss={NestedPanelHeadingTitle}>
					{titleLabel}
					{afterHeadingNode}
					{pinnable && <FieldPin fieldId={DEV_SUMMARY_TYPE} label={titleLabel} />}
				</Flex>
			}
			onToggle={onToggleNestedPanel}
			showBottomSeparator={false}
			hideTitleOnExpand
			title={<></>}
			prefetch
		>
			<Box xcss={NestedPanelChildrenStyles}>{children}</Box>
		</NestedPanel>
	);
};

export default componentWithCondition(
	() => expVal('jiv-21227_dev_panel_collapsibility_exp', 'isEnabled', false),
	DevelopmentFieldWrapperNext,
	DevelopmentFieldWrapper,
);

const DevPanelFieldHeadingNew = ({ children }: { children: ReactNode }) => {
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const isWideViewLayout = userPreferredLayout === 'WIDE';
	return (
		<Box xcss={[fieldHeadingSideBySideCssStylesNew, isWideViewLayout && wideLayoutStyles]}>
			<Box
				xcss={[
					devPanelFieldHeadingOuterStyles,
					isWideViewLayout && devPanelFieldHeadingOuterStylesWideLayoutOverride,
				]}
			>
				{children}
			</Box>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DevPanelFieldHeadingOld = styled(FieldHeadingSideBySide)({
	display: 'flex',
	paddingRight: '0px',
});

const fieldHeadingSideBySideCssStylesNew = xcss({
	position: 'relative',
	boxSizing: 'border-box',
	minWidth: '120px',
	width: '40%',
	flex: '1 0 0%',
	display: 'flex',
	paddingRight: 'space.200',
	paddingTop: 'space.100',
});

const wideLayoutStyles = xcss({
	maxWidth: '170px',
	paddingRight: 'space.0',
	paddingLeft: 'space.050',
});

const devPanelFieldHeadingOuterStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	height: 'fit-content',
	paddingBottom: 'space.100',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	borderRadius: token('radius.small'),
	width: 'fit-content',
	marginLeft: 'space.negative.050',
	gap: 'space.100',
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},
});

const devPanelFieldHeadingOuterStylesWideLayoutOverride = xcss({
	minWidth: '170px',
	paddingTop: 'space.050',
	paddingLeft: 'space.0',
	justifyContent: 'flex-start',
	marginTop: 'space.negative.050',
});

const DevPanelFieldHeading = componentWithFG(
	'issue_view_field_config_edit',
	DevPanelFieldHeadingNew,
	DevPanelFieldHeadingOld,
);

const NestedPanelHeadingTitle = xcss({
	display: 'flex',
	flexGrow: 1,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: '0px',
	gap: 'space.100',
});

const NestedPanelChildrenStyles = xcss({
	// I don't love this, but it's the only way to get the  icons of the children to be lined up with the field title.
	// i _could_ add a negative margin and calc width to the children, but this introduces less complexity overall (IMO).
	// I could also remove padding left from the children, but this just makes the button styles inconsistent
	width: `calc(100% + ${token('space.050')})`,
	marginLeft: 'space.negative.050',
});
